// src/components/common/ColoredComments.jsx
import {
  colorClassName,
  isValidSegments,
  sliceSegments,
} from "../../utils/coloredText";

/**
 * ColoredComments — read-only render of a measurement note.
 *
 * Renders `text` with the colouring described by `segments` (see the data
 * contract in src/utils/coloredText.js). Falls back to the plain string,
 * character-for-character identical to the old render, whenever:
 *   - there are no segments (every room that predates this feature), or
 *   - the segments don't join back to exactly `text` (defensive — a note is
 *     never shown wrong or duplicated because of a bad write).
 *
 * Emits only text nodes and <span>s, so it drops into any existing <p>/<td>
 * without changing layout. Truncation is by PLAIN character count first, then
 * the segments are clipped to the same length — so the 100-char behaviour in
 * the Rooms table is exactly what it was.
 *
 * NOT used on invoices. Invoice screens render `room.comments` plain by design.
 *
 * @param {Object} props
 * @param {string} props.text - The plain note (`room.comments`)
 * @param {Array} [props.segments] - `room.commentsSegments`, if present
 * @param {number|null} [props.maxLength] - Truncate to N plain chars, adding "..."
 */
const ColoredComments = ({ text = "", segments, maxLength = null }) => {
  const plain = text || "";
  const isTruncated = maxLength != null && plain.length > maxLength;
  const ellipsis = isTruncated ? "..." : "";

  // Plain path — identical output to the pre-feature render.
  if (!isValidSegments(segments, plain)) {
    return <>{(isTruncated ? plain.slice(0, maxLength) : plain) + ellipsis}</>;
  }

  const shown = isTruncated ? sliceSegments(segments, 0, maxLength) : segments;

  return (
    <>
      {shown.map((segment, index) => (
        <span key={index} className={colorClassName(segment.color)}>
          {segment.text}
        </span>
      ))}
      {ellipsis}
    </>
  );
};

export default ColoredComments;
